import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const ITEMS_PER_PAGE = 10;

export const useActivityData = (api, userId = null) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // 단순히 page 파라미터 사용
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await (userId ? api(userId) : api());
      setAllData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch activity data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleItemDelete = useCallback(
    (itemId, itemCategory) => {
      // 리뷰 삭제인 경우 TanStack Query 캐시 무효화
      if (
        itemCategory === 'Reviews' ||
        (itemId && allData.find((item) => item.reviewId === itemId))
      ) {
        queryClient.invalidateQueries({ queryKey: ['albumReviews'] });
        queryClient.invalidateQueries({ queryKey: ['albumDetail'] });
      }

      setAllData((prevData) =>
        prevData.filter((item) => {
          if (item.category && itemCategory) {
            // FIXME: 이전 코드와 호환용 구현 제거 필요
            const idMatch = (item.comment?.id ?? item.commentId ?? item.id) === itemId;
            return !(idMatch && item.category === itemCategory);
          }
          if (item.reviewId) {
            return item.reviewId !== itemId;
          }
          return item.id !== itemId;
        }),
      );
    },
    [queryClient, allData],
  );

  const pageCount = useMemo(() => Math.ceil(allData.length / ITEMS_PER_PAGE), [allData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return allData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [allData, currentPage]);

  const handlePageChange = useCallback(
    (event, value) => {
      // 현재 URL의 모든 파라미터를 유지하면서 페이지 정보만 업데이트
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', value.toString());
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  return {
    data: paginatedData,
    loading,
    totalCount: allData.length,
    page: currentPage,
    pageCount,
    handlePageChange,
    handleItemDelete,
    refetch: fetchData,
  };
};
